import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { toast } from 'react-toastify'
import useSettings from '../../hooks/useSettings'

const actionLabel = {
  insert: 'Insert',
  update: 'Update',
  delete: 'Delete'
}

const escapeCsvValue = value => {
  const normalizedValue = value === null || value === undefined ? '' : String(value)
  return `"${normalizedValue.replace(/"/g, '""')}"`
}

const SettingsAuditLogs = () => {
  const { getAuditLogs } = useSettings()

  const [records, setRecords] = useState([])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [action, setAction] = useState('')

  const loadLogs = useCallback(
    async (page = 1, reset = false) => {
      try {
        setLoading(true)
        const response = await getAuditLogs({
          pageNumber: String(page),
          searchParam,
          action
        })

        const loadedRecords = response?.records || []
        setRecords(prev => (reset ? loadedRecords : [...prev, ...loadedRecords]))
        setHasMore(Boolean(response?.hasMore))
        setPageNumber(page)
      } catch (error) {
        toast.error('Nao foi possivel carregar o historico de auditoria.')
      } finally {
        setLoading(false)
      }
    },
    [action, getAuditLogs, searchParam]
  )

  useEffect(() => {
    loadLogs(1, true)
  }, [loadLogs])

  const handleSearch = () => {
    loadLogs(1, true)
  }

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadLogs(pageNumber + 1)
    }
  }

  const handleExportCsv = () => {
    if (!records.length) {
      toast.info('Nao ha dados para exportar.')
      return
    }

    const header = [
      'DataHora',
      'Usuario',
      'Acao',
      'Chave',
      'ValorAnterior',
      'ValorNovo',
      'IP'
    ]

    const rows = records.map(record => [
      record.createdAt ? new Date(record.createdAt).toLocaleString('pt-BR') : '',
      record.username || '',
      actionLabel[record.action] || record.action || '',
      record.settingKey || '',
      record.oldValue || '',
      record.newValue || '',
      record.ipAddress || ''
    ])

    const csvContent = [header, ...rows]
      .map(columns => columns.map(escapeCsvValue).join(';'))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const now = new Date()
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`

    link.href = url
    link.setAttribute('download', `settings-audit-${timestamp}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <Paper elevation={0} style={{ padding: 16 }}>
      <Typography variant="h6" style={{ marginBottom: 8 }}>
        Auditoria de Settings
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Visualize quem alterou configuracoes e qual acao foi executada (insert, update, delete).
      </Typography>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Buscar por usuario ou chave"
            value={searchParam}
            onChange={event => setSearchParam(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Acao</InputLabel>
            <Select
              value={action}
              onChange={event => setAction(event.target.value)}
              label="Acao"
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="insert">Insert</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading}
          >
            Filtrar
          </Button>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleExportCsv}
            disabled={loading || !records.length}
          >
            Exportar CSV
          </Button>
        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small" aria-label="settings audit logs table">
          <TableHead>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Acao</TableCell>
              <TableCell>Chave</TableCell>
              <TableCell>Valor Anterior</TableCell>
              <TableCell>Valor Novo</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map(record => (
              <TableRow key={record.id} hover>
                <TableCell>
                  {record.createdAt
                    ? new Date(record.createdAt).toLocaleString('pt-BR')
                    : '-'}
                </TableCell>
                <TableCell>{record.username || '-'}</TableCell>
                <TableCell>{actionLabel[record.action] || record.action || '-'}</TableCell>
                <TableCell>{record.settingKey || '-'}</TableCell>
                <TableCell>{record.oldValue || '-'}</TableCell>
                <TableCell>{record.newValue || '-'}</TableCell>
                <TableCell>{record.ipAddress || '-'}</TableCell>
              </TableRow>
            ))}
            {!records.length && !loading && (
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  Nenhum log encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {hasMore && (
        <Box display="flex" justifyContent="center" marginTop={2}>
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading}>
            {loading ? 'Carregando...' : 'Carregar mais'}
          </Button>
        </Box>
      )}
    </Paper>
  )
}

export default SettingsAuditLogs
